import { useUserPhone } from '@terminal/react-query-hooks/user-phone/useUserPhone';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useSubscription } from '@terminal/stores/use-subscription/useSubscription';
import { buildWhatsAppAccountLink } from '@terminal/views/settings/account-view/components/whatsapp-alfred/whatsapp-account-link';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';

import styles from './ScheduledQueryDeliveryStatus.module.scss';

interface ScheduledQueryDeliveryStatusProps {
  className?: string;
  openInNewTab?: boolean;
}

export const ScheduledQueryDeliveryStatus = ({
  className,
  openInNewTab = false,
}: ScheduledQueryDeliveryStatusProps) => {
  const { t } = useTranslation(['chat']);
  const location = useLocation();
  const isFeatureEnabled = useFlags(state =>
    state.getFeatureFlagState('alfred-whatsapp-integration')
  );
  const {
    data: phoneStatus,
    isLoading,
    isError,
  } = useUserPhone(isFeatureEnabled);
  const isBasicUser = useSubscription(state => state.isBasicUser());

  if (!isFeatureEnabled || isLoading || (isError && !phoneStatus)) {
    return null;
  }

  const isVerified = phoneStatus?.verified === true;
  const showSetup = !isVerified && !isBasicUser;
  const returnTo = `${location.pathname}${location.search}`;
  const accountLink = buildWhatsAppAccountLink(returnTo);

  return (
    <div
      className={clsx(styles.root, className)}
      data-testid="scheduled-query-delivery-status"
    >
      <span className={styles.label}>
        {t('chat:scheduledQuerySettings.deliveryLabel')}
      </span>
      <span className={styles.destination}>
        {isVerified
          ? t('chat:scheduledQuerySettings.deliveryEmailWhatsApp', {
              phone: phoneStatus.phone_masked,
            })
          : t('chat:scheduledQuerySettings.deliveryEmailOnly')}
      </span>
      {(isVerified || showSetup) && (
        <Link
          className={styles.link}
          to={accountLink}
          {...(openInNewTab
            ? { target: '_blank', rel: 'noopener noreferrer' }
            : {})}
        >
          {t(
            isVerified
              ? 'chat:scheduledQuerySettings.deliveryManage'
              : 'chat:scheduledQuerySettings.deliverySetUp'
          )}
        </Link>
      )}
    </div>
  );
};
